import React, { useEffect, useState } from 'react';
import './ProductDetail.css';
import './ExploreProducts.css';
import { useDispatch, useSelector } from 'react-redux';
import { addToCart, selectLanguage } from '../redux/cartSlice';
import { translations } from '../utils/translations';
import { translateText } from '../utils/translator';
import useWishlist from '../hooks/useWishlist';

const detailTranslations = {
  en: {
    serviceCost: "Service Cost",
    termsConditions: "Terms & Conditions",
    brandName: "Brand Name",
    model: "Model",
    fault: "Fault",
    deliveryTime: "Delivery Time",
    specifications: "Specifications"
  },
  nl: {
    serviceCost: "Servicekosten",
    termsConditions: "Algemene Voorwaarden",
    brandName: "Merk",
    model: "Model",
    fault: "Defect",
    deliveryTime: "Levertijd",
    specifications: "Specificaties"
  },
  es: {
    serviceCost: "Costo del Servicio",
    termsConditions: "Términos y Condiciones",
    brandName: "Nombre de la Marca",
    model: "Modelo",
    fault: "Fallo",
    deliveryTime: "Tiempo de Entrega",
    specifications: "Especificaciones"
  },
  fr: {
    serviceCost: "Coût du Service",
    termsConditions: "Conditions Générales",
    brandName: "Nom de la Marque",
    model: "Modèle",
    fault: "Panne",
    deliveryTime: "Délai de Livraison",
    specifications: "Spécifications"
  },
  de: {
    serviceCost: "Servicekosten",
    termsConditions: "Allgemeine Geschäftsbedingungen",
    brandName: "Markenname",
    model: "Modell",
    fault: "Fehler",
    deliveryTime: "Lieferzeit",
    specifications: "Spezifikationen"
  },
  it: {
    serviceCost: "Costo del Servizio",
    termsConditions: "Termini e Condizioni",
    brandName: "Nome del Brand",
    model: "Modello",
    fault: "Guasto",
    deliveryTime: "Tempi di Consegna",
    specifications: "Specifiche"
  },
  pt: {
    serviceCost: "Custo do Serviço",
    termsConditions: "Termos e Condições",
    brandName: "Nome da Marca",
    model: "Modelo",
    fault: "Falha",
    deliveryTime: "Prazo de Entrega",
    specifications: "Especificações"
  },
  el: {
    serviceCost: "Κόστος Υπηρεσίας",
    termsConditions: "Όροι & Προϋποθέσεις",
    brandName: "Όνομα Μάρκας",
    model: "Μοντέλο",
    fault: "Βλάβη",
    deliveryTime: "Χρόνος Παράδοσης",
    specifications: "Προδιαγραφές"
  }
};

const ProductDetail = ({ productId }) => {
  const dispatch = useDispatch();
  const selectedLang = useSelector(selectLanguage) || { code: 'en' };
  const t = translations[selectedLang.code || 'en'] || translations.en;
  const dt = detailTranslations[selectedLang.code || 'en'] || detailTranslations.en;
  const getCategoryTranslation = (name) => {
    if (!name) return '';
    const lower = name.toLowerCase().trim();
    if (lower === 'mobile phone') return t.categoryMobilePhone || name;
    if (lower === 'accessories') return t.categoryAccessories || name;
    if (lower === 'gadget') return t.categoryFormat || t.categoryGadget || name;
    if (lower === 'service' || lower === 'services') return t.categoryService || name;
    if (lower === 'parts') return t.categoryParts || name;
    if (lower === 'computers') return t.categoryComputers || name;
    if (lower === 'offers') return t.categoryOffers || name;
    if (lower === 'used phone') return t.categoryUsedPhone || name;
    if (lower === 'electronics') return t.categoryElectronics || name;
    return name;
  };
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState({ description: true, shipping: true });
  const [activeThumb, setActiveThumb] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [translatedName, setTranslatedName] = useState('');
  const [translatedDesc, setTranslatedDesc] = useState('');
  const [translatedSpec, setTranslatedSpec] = useState('');
  const [translatedFault, setTranslatedFault] = useState('');
  const [translatedDeliveryTime, setTranslatedDeliveryTime] = useState('');
  const [translatedRelatedNames, setTranslatedRelatedNames] = useState({});
  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const [isSpecExpanded, setIsSpecExpanded] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [zoomScale, setZoomScale] = useState(1);
  const [user, setUser] = useState(() => {
    try {
      const session = localStorage.getItem('user_session');
      return session ? JSON.parse(session) : null;
    } catch { return null; }
  });
  const { isWishlisted, toggleWishlist } = useWishlist();

  useEffect(() => {
    const loadSession = () => {
      try {
        const session = localStorage.getItem('user_session');
        setUser(session ? JSON.parse(session) : null);
      } catch { setUser(null); }
    };
    window.addEventListener('auth_change', loadSession);
    window.addEventListener('storage', loadSession);
    return () => {
      window.removeEventListener('auth_change', loadSession);
      window.removeEventListener('storage', loadSession);
    };
  }, []);

  const handleAddToCart = (p, qty = 1, e) => {
    if (e) e.stopPropagation();

    const isService = p.isService || (p.category && p.category.toLowerCase().trim() === 'services');
    let priceToUse = p.price;
    let b2bPriceToUse = p.b2bPrice;

    if (isService) {
      const sCost = p.serviceCost !== undefined ? Number(p.serviceCost) : 0;
      priceToUse = Number(p.price) + sCost;
      b2bPriceToUse = (p.b2bPrice !== undefined ? Number(p.b2bPrice) : Number(p.price)) + sCost;
    }

    dispatch(addToCart({
      id: p._id || p.id,
      name: p.name,
      price: priceToUse,
      b2bPrice: b2bPriceToUse,
      originalPrice: isService ? undefined : p.originalPrice,
      image: p.image,
      quantity: qty,
      category: p.category,
      shippingCharges: p.shippingCharges,
      deliveryTime: p.deliveryTime
    }));

    import('sweetalert2').then((Swal) => {
      const Toast = Swal.default.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', Swal.default.stopTimer);
          toast.addEventListener('mouseleave', Swal.default.resumeTimer);
        }
      });
      const getAddedToCartMessage = (name) => {
        switch (selectedLang.code) {
          case 'nl': return `${name} toegevoegd aan winkelwagen!`;
          case 'es': return `¡${name} añadido al carrito!`;
          case 'fr': return `${name} ajouté au panier !`;
          case 'de': return `${name} zum Warenkorb hinzugefügt!`;
          case 'it': return `${name} aggiunto al carrello!`;
          case 'pt': return `${name} adicionado ao carrinho!`;
          case 'el': return `${name} προστέθηκε στο καλάθι!`;
          default: return `${name} added to cart!`;
        }
      };
      Toast.fire({
        icon: 'success',
        title: getAddedToCartMessage(p.name)
      });
    });
  };

  const handleBuyNow = (p, e) => {
    if (e) e.stopPropagation();

    const isService = p.isService || (p.category && p.category.toLowerCase().trim() === 'services');
    let priceToUse = p.price;
    let b2bPriceToUse = p.b2bPrice;

    if (isService) {
      const sCost = p.serviceCost !== undefined ? Number(p.serviceCost) : 0;
      priceToUse = Number(p.price) + sCost;
      b2bPriceToUse = (p.b2bPrice !== undefined ? Number(p.b2bPrice) : Number(p.price)) + sCost;
    }

    dispatch(addToCart({
      id: p._id || p.id,
      name: p.name,
      price: priceToUse,
      b2bPrice: b2bPriceToUse,
      originalPrice: isService ? undefined : p.originalPrice,
      image: p.image,
      quantity: 1,
      category: p.category,
      shippingCharges: p.shippingCharges,
      deliveryTime: p.deliveryTime
    }));
    window.history.pushState({}, '', '/cart');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleDecrease = () => {
    if (quantity > 1) setQuantity(quantity - 1);
  };

  const handleIncrease = () => {
    if (product && !product.isService && product.quantity !== undefined && quantity >= product.quantity) {
      import('sweetalert2').then((Swal) => {
        Swal.default.fire({
          icon: 'warning',
          title: 'Stock Limit Reached',
          text: `Only ${product.quantity} items are available in stock.`,
          confirmButtonColor: '#be1e2d'
        });
      });
      return;
    }
    setQuantity(quantity + 1);
  };

  useEffect(() => {
    const loadProductAndRelated = async () => {
      setLoading(true);
      try {
        // 1. Fetch current product
        let foundProduct = null;
        if (productId) {
          try {
            const res = await fetch(`/api/products/${productId}`);
            if (res.ok) {
              foundProduct = await res.json();
            } else {
              // Try fetching as a service since product wasn't found
              const serviceRes = await fetch(`/api/services/${productId}`);
              if (serviceRes.ok) {
                foundProduct = await serviceRes.json();
                if (foundProduct) {
                  foundProduct.isService = true;
                }
              } else {
                // Try fetching as a brand product
                const brandRes = await fetch(`/api/brand-products/${productId}`);
                if (brandRes.ok) {
                  foundProduct = await brandRes.json();
                }
              }
            }
          } catch (err) {
            console.error("API error fetching product/service details:", err);
          }
        }

        setProduct(foundProduct);
        setActiveThumb(0);
        setQuantity(1);
        setIsDescExpanded(false);
        setIsSpecExpanded(false);

        // Update page title & SEO meta description
        if (foundProduct) {
          document.title = foundProduct.metaTitle || `${foundProduct.name} | Smart Telecom Store`;

          let metaDescTag = document.querySelector('meta[name="description"]');
          if (foundProduct.metaDescription) {
            if (!metaDescTag) {
              metaDescTag = document.createElement('meta');
              metaDescTag.name = 'description';
              document.head.appendChild(metaDescTag);
            }
            metaDescTag.content = foundProduct.metaDescription;
          } else if (metaDescTag) {
            metaDescTag.content = foundProduct.description || 'Smart Telecom Store product details';
          }
        }

        // 2. Load assigned related products from the product details
        if (foundProduct && foundProduct.relatedProducts && foundProduct.relatedProducts.length > 0) {
          setRelatedProducts(foundProduct.relatedProducts.slice(0, 8));
        } else {
          setRelatedProducts([]);
        }
      } catch (err) {
        console.error("Failed to load product details:", err);
      } finally {
        setLoading(false);
      }
    };

    loadProductAndRelated();
    window.scrollTo(0, 0);
  }, [productId]);

  useEffect(() => {
    if (!product) return;

    let isMounted = true;

    const runTranslation = async () => {
      const targetLang = selectedLang.code || 'en';
      if (targetLang === 'en') {
        if (isMounted) {
          setTranslatedName(product.name || '');
          setTranslatedDesc(product.description || '');
          setTranslatedSpec(product.specification || '');
          setTranslatedFault(product.fault || '');
          setTranslatedDeliveryTime(product.deliveryTime || '');
          setTranslatedRelatedNames({});
        }
      } else {
        const [name, desc, spec, fault, delTime] = await Promise.all([
          translateText(product.name, targetLang, 'en'),
          translateText(product.description, targetLang, 'en'),
          translateText(product.specification, targetLang, 'en'),
          translateText(product.fault, targetLang, 'en'),
          translateText(product.deliveryTime, targetLang, 'en')
        ]);

        if (isMounted) {
          setTranslatedName(name || product.name || '');
          setTranslatedDesc(desc || product.description || '');
          setTranslatedSpec(spec || product.specification || '');
          setTranslatedFault(fault || product.fault || '');
          setTranslatedDeliveryTime(delTime || product.deliveryTime || '');
        }

        if (relatedProducts && relatedProducts.length > 0) {
          const relMap = {};
          await Promise.all(
            relatedProducts.map(async (rp) => {
              const rId = rp._id || rp.id;
              if (rId && rp.name) {
                relMap[rId] = await translateText(rp.name, targetLang, 'en');
              }
            })
          );
          if (isMounted) {
            setTranslatedRelatedNames(relMap);
          }
        }
      }
    };

    runTranslation();

    return () => {
      isMounted = false;
    };
  }, [product, relatedProducts, selectedLang.code]);

  const productImages = product ? ((product.images && product.images.length > 0) ? product.images : [product.image]) : [];
  const mediaItems = productImages.map((img) => ({ type: 'image', url: img }));
  if (product && product.videoUrl) {
    mediaItems.push({ type: 'video', url: product.videoUrl });
  }

  useEffect(() => {
    if (!isLightboxOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsLightboxOpen(false);
      } else if (e.key === 'ArrowRight') {
        setLightboxIndex(prev => (prev === mediaItems.length - 1 ? 0 : prev + 1));
        setZoomScale(1);
      } else if (e.key === 'ArrowLeft') {
        setLightboxIndex(prev => (prev === 0 ? mediaItems.length - 1 : prev - 1));
        setZoomScale(1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLightboxOpen, mediaItems]);

  const handleProductClick = (id) => {
    window.history.pushState({}, '', `/product/${id}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating || 5);
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={i <= fullStars ? 'star-filled' : 'star-empty'}>★</span>
      );
    }
    return stars;
  };

  if (loading) {
    return (
      <div className="product-detail-page-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div className="modern-loading-screen" style={{ position: 'relative', background: 'transparent' }}>
          <div className="loader-orbit">
            <div className="orbit-item"></div>
            <div className="orbit-item"></div>
          </div>
          <span className="loader-text">Loading Product Details...</span>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-page-wrapper" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '65vh', gap: '24px', textAlign: 'center' }}>
        <h2 style={{ color: '#be1e2d', fontSize: '2rem', fontWeight: '700', margin: '0' }}>Product Not Found</h2>
        <p style={{ color: '#718096', fontSize: '1rem', maxWidth: '400px', margin: '0' }}>The product you are trying to view does not exist or has been removed from the catalog.</p>
        <button
          onClick={() => {
            window.history.pushState({}, '', '/');
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
          className="action-submit-btn"
          style={{ width: 'auto', padding: '12px 32px' }}
        >
          Return to Store
        </button>
      </div>
    );
  }

  const displayOriginalPrice = product.originalPrice ? (typeof product.originalPrice === 'number' ? `$${product.originalPrice.toFixed(2)}` : product.originalPrice) : null;
  const needsDescCollapse = translatedDesc && (translatedDesc.length > 300 || translatedDesc.split('\n').length > 5);
  const needsSpecCollapse = translatedSpec && (translatedSpec.length > 300 || translatedSpec.split('\n').length > 5);

  // Calculate discount percentage if original price is present
  let discountPercent = 0;
  if (displayOriginalPrice) {
    const priceNum = typeof product.price === 'number' ? product.price : parseFloat(product.price.replace(/[^0-9.]/g, ''));
    const originalNum = typeof product.originalPrice === 'number' ? product.originalPrice : parseFloat(product.originalPrice.replace(/[^0-9.]/g, ''));
    if (originalNum > priceNum) {
      discountPercent = Math.round(((originalNum - priceNum) / originalNum) * 100);
    }
  }

  return (
    <div className="product-detail-page-wrapper">
      <div className="product-detail-container">

        {/* Left Column: Image Gallery */}
        <div className="detail-gallery-col">
          <div 
            className="main-image-container"
            onClick={() => {
              if (mediaItems[activeThumb]?.type !== 'video') {
                setLightboxIndex(activeThumb);
                setIsLightboxOpen(true);
                setZoomScale(1);
              }
            }}
            style={{ cursor: mediaItems[activeThumb]?.type !== 'video' ? 'zoom-in' : 'default' }}
          >
            {discountPercent > 0 && (
              <span className="detail-discount-badge">{discountPercent}% OFF</span>
            )}
            {mediaItems[activeThumb]?.type === 'video' ? (
              <video
                key={mediaItems[activeThumb].url}
                src={mediaItems[activeThumb].url}
                controls
                autoPlay
                muted
                playsInline
                className="gallery-main-video"
                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', borderRadius: '8px' }}
              />
            ) : (
              <img
                src={mediaItems[activeThumb]?.url}
                alt={product.name}
                className="gallery-main-img"
              />
            )}
          </div>

          {/* Thumbnails Row */}
          <div className="thumbnails-row">
            {mediaItems.map((item, idx) => (
              <div
                key={idx}
                className={`thumbnail-card ${idx === activeThumb ? 'selected' : ''} ${item.type === 'video' ? 'video-thumbnail-card' : ''}`}
                onClick={() => setActiveThumb(idx)}
                style={{ position: 'relative', overflow: 'hidden' }}
              >
                {item.type === 'video' ? (
                  <div className="thumb-video-wrapper" style={{ width: '100%', height: '100%', position: 'relative' }}>
                    <video
                      src={item.url}
                      muted
                      loop
                      autoPlay
                      playsInline
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    <div style={{ position: 'absolute', bottom: '6px', right: '6px', background: 'rgba(0,0,0,0.6)', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ color: '#fff' }}>
                        <polygon points="5 3 19 12 5 21 5 3" fill="#fff" />
                      </svg>
                    </div>
                  </div>
                ) : (
                  <img src={item.url} alt={`View ${idx}`} className="thumb-img" />
                )}
              </div>
            ))}
          </div>
          
          {/* Related Products Section */}
          {relatedProducts.length > 0 && (
            <div className="related-products-section">
              <h2 className="related-products-title">{t.relatedProducts}</h2>
              <div className="wavy-line-container">
                <svg className="wavy-line" width="40" height="10" viewBox="0 0 40 10" fill="none">
                  <path d="M2 5C6 1 8 9 12 5C16 1 18 9 22 5C26 1 28 9 32 5C36 1 38 9 42 5" stroke="#be1e2d" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="products-grid-four">
                {relatedProducts.map((prod) => {
                  const pId = prod._id || prod.id;
                  const isB2b = user && user.isB2B;
                  const isService = prod.isService || (prod.category && prod.category.toLowerCase().trim() === 'services');
                  const activePrice = (isB2b && prod.b2bPrice) ? prod.b2bPrice : prod.price;

                  const rPrice = typeof activePrice === 'number' ? `€${activePrice.toFixed(2)}` : activePrice;
                  const rOriginalPrice = (isB2b && prod.b2bPrice)
                    ? `€${prod.price.toFixed(2)}`
                    : (prod.originalPrice ? (typeof prod.originalPrice === 'number' ? `€${prod.originalPrice.toFixed(2)}` : prod.originalPrice) : null);
                  const rImg = (prod.images && prod.images.length > 0) ? prod.images[0] : prod.image;

                  return (
                    <div key={pId} className="product-card" onClick={() => handleProductClick(pId)}>
                      <div className="product-image-container">
                        {rOriginalPrice && (
                          <span className="product-badge sale">SALE!</span>
                        )}
                        {/* Action Buttons Overlay */}
                        <div className="product-actions-overlay">
                          <button
                            className={`action-btn wishlist-btn${isWishlisted(pId) ? ' wishlisted' : ''}`}
                            onClick={(e) => { e.stopPropagation(); toggleWishlist({ productId: pId, name: prod.name || '', image: (prod.images && prod.images.length > 0) ? prod.images[0] : (prod.image || ''), price: prod.price || 0, category: prod.category || '' }); }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill={isWishlisted(pId) ? '#be1e2d' : 'none'} stroke={isWishlisted(pId) ? '#be1e2d' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'fill 0.2s ease, stroke 0.2s ease' }}>
                              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                            </svg>
                          </button>
                          <button
                            className="action-btn"
                            aria-label="Quick View"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProductClick(pId);
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </button>
                        </div>

                        <div className="product-img-wrapper">
                          <img src={rImg} alt={prod.name} className="product-card-img" />
                        </div>
                      </div>

                      <div className="product-info">
                        <span className="product-cat">{getCategoryTranslation(prod.category)}</span>
                        <h3 className="product-name" title={translatedRelatedNames[pId] || prod.name}>{translatedRelatedNames[pId] || prod.name}</h3>

                        {/* Rating & Reviews */}
                        <div className="product-rating-row">
                          <div className="stars-container">{renderStars(prod.rating)}</div>
                          <span className="reviews-count">({prod.rating || 0})</span>
                        </div>

                        {/* Price block */}
                        <div className="product-price-row" style={isService ? { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px', marginTop: 'auto' } : {}}>
                          {isService ? (
                            <>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '14px', color: '#718096', fontWeight: '600' }}>Price:</span>
                                <span className="current-price" style={{ fontSize: '16px', color: (isB2b && prod.b2bPrice) ? '#be1e2d' : undefined }}>{rPrice}</span>
                                {rOriginalPrice && (
                                  <span className="original-price" style={{ textDecoration: 'line-through', fontSize: '0.85rem', color: '#a0aec0' }}>{rOriginalPrice}</span>
                                )}
                                {isB2b && prod.b2bPrice && (
                                  <span className="b2b-price-badge" style={{
                                    fontSize: '0.65rem',
                                    fontWeight: '700',
                                    backgroundColor: '#be1e2d',
                                    color: 'white',
                                    padding: '1px 5px',
                                    borderRadius: '4px',
                                    textTransform: 'uppercase'
                                  }}>B2B Price</span>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ fontSize: '14px', color: '#718096', fontWeight: '600' }}>Service Cost:</span>
                                <span className="current-price">€{prod.serviceCost}</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <span className="current-price" style={isB2b && prod.b2bPrice ? { color: '#be1e2d' } : {}}>{rPrice}</span>
                              {rOriginalPrice && (
                                <span className="original-price">{rOriginalPrice}</span>
                              )}
                              {isB2b && prod.b2bPrice && (
                                <span className="b2b-price-badge" style={{
                                  fontSize: '0.65rem',
                                  fontWeight: '700',
                                  backgroundColor: '#be1e2d',
                                  color: 'white',
                                  padding: '1px 5px',
                                  borderRadius: '4px',
                                  marginLeft: '6px',
                                  verticalAlign: 'middle',
                                  textTransform: 'uppercase'
                                }}>B2B Price</span>
                              )}
                            </>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="product-card-buttons" onClick={(e) => e.stopPropagation()}>
                          <button
                            className="btn-add-to-cart"
                            aria-label="Add to Cart"
                            onClick={(e) => handleAddToCart(prod, 1, e)}
                            disabled={!prod.isService && (prod.stockStatus === 'Out of Stock' || prod.quantity <= 0)}
                            style={(!prod.isService && (prod.stockStatus === 'Out of Stock' || prod.quantity <= 0)) ? { opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#718096' } : {}}
                          >
                            {(!prod.isService && (prod.stockStatus === 'Out of Stock' || prod.quantity <= 0)) ? t.outOfStock : t.addToCart}
                          </button>
                          <button
                            className="btn-buy-now"
                            aria-label="Buy Now"
                            onClick={(e) => handleBuyNow(prod, e)}
                            disabled={!prod.isService && (prod.stockStatus === 'Out of Stock' || prod.quantity <= 0)}
                            style={(!prod.isService && (prod.stockStatus === 'Out of Stock' || prod.quantity <= 0)) ? { opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#718096' } : {}}
                          >
                            {t.buyNow}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Info & Purchase Controls */}
        <div className="detail-info-col">
          <span className="info-category-tag">{getCategoryTranslation(product.category)}</span>
          <h1 className="info-product-name">{translatedName || product.name}</h1>

          <div className="product-rating-stock-row">
            <span className="star-rating">
              {renderStars(product.rating)}
            </span>
            {!(product.isService || (product.category && product.category.toLowerCase().trim() === 'services')) && (
              <>
                <span className="reviews-label-text">({product.rating || 0} {t.reviews})</span>
              </>
            )}
            <span className="divider-vertical-bar">|</span>
            <span className={(!product.isService && (product.stockStatus === 'Out of Stock' || product.quantity <= 0)) ? "stock-badge-red" : "stock-badge-green"}>
              {(!product.isService && (product.stockStatus === 'Out of Stock' || product.quantity <= 0)) ? t.outOfStock : t.inStock}
            </span>
          </div>

          <div className="info-product-price">
            {(() => {
              const isB2b = user && user.isB2B;
              const activePrice = (isB2b && product.b2bPrice) ? product.b2bPrice : product.price;
              const hasB2bDiscount = isB2b && product.b2bPrice;

              return (
                <>
                  €{(activePrice * quantity).toFixed(2)}
                  {hasB2bDiscount ? (
                    <>
                      <span className="detail-original-price" style={{ textDecoration: 'line-through', fontSize: '1.1rem', color: '#a0aec0', marginLeft: '12px' }}>€{(product.price * quantity).toFixed(2)}</span>
                      <span className="b2b-price-badge" style={{
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        backgroundColor: '#be1e2d',
                        color: 'white',
                        padding: '3px 8px',
                        borderRadius: '4px',
                        marginLeft: '12px',
                        verticalAlign: 'middle',
                        textTransform: 'uppercase'
                      }}>B2B Price</span>
                    </>
                  ) : (
                    product.originalPrice && <span className="detail-original-price" style={{ textDecoration: 'line-through', fontSize: '1.1rem', color: '#a0aec0', marginLeft: '12px' }}>€{(product.originalPrice * quantity).toFixed(2)}</span>
                  )}
                  {quantity > 1 && <span style={{ fontSize: '0.85rem', color: '#718096', marginLeft: '10px', fontWeight: '500' }}>({quantity} × €{activePrice.toFixed(2)})</span>}
                </>
              );
            })()}
          </div>

          {(product.isService || (product.category && product.category.toLowerCase() === 'services')) && product.serviceCost !== undefined && (
            <div className="detail-service-cost-block" style={{ margin: '15px 0 10px 0', padding: '12px 16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #edf2f7', display: 'inline-block' }}>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#718096', fontWeight: '700' }}>{dt.serviceCost}</h4>
              <span style={{ fontSize: '18px', fontWeight: '700', color: '#be1e2d' }}>€{product.serviceCost.toFixed(2)}</span>
            </div>
          )}

          {/* Description below price */}
          <div className="info-product-description" style={{ margin: '20px 0', color: '#4a5568', fontSize: '15px', lineHeight: '1.6' }}>
            {product.isService || (product.category && product.category.toLowerCase() === 'services') ? (
              <>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '700', color: '#1a202c' }}>{dt.termsConditions}</h4>
                <p style={!isDescExpanded && needsDescCollapse ? {
                  display: '-webkit-box',
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                } : {}}>{translatedDesc}</p>
              </>
            ) : (
              <p style={!isDescExpanded && needsDescCollapse ? {
                display: '-webkit-box',
                WebkitLineClamp: 5,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              } : {}}>{translatedDesc}</p>
            )}
            
            {needsDescCollapse && (
              <div style={{ marginTop: '8px' }}>
                {!isDescExpanded ? (
                  <button 
                    onClick={() => setIsDescExpanded(true)} 
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      color: '#be1e2d', 
                      fontWeight: '600', 
                      cursor: 'pointer', 
                      padding: 0,
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    Show more
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                ) : (
                  <button 
                    onClick={() => setIsDescExpanded(false)} 
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      color: '#be1e2d', 
                      fontWeight: '600', 
                      cursor: 'pointer', 
                      padding: 0,
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    Show less
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="18 15 12 9 6 15"></polyline>
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Service Extra Details */}
          {(product.brandName || product.model || product.fault || product.deliveryTime || product.brandLogo) && (
            <div className="service-details-box" style={{
              margin: '20px 0',
              padding: '16px',
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '20px',
              fontSize: '14px',
              color: '#4a5568'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', flexGrow: 1 }}>
                {product.brandName && <div><strong>{dt.brandName}:</strong> {product.brandName}</div>}
                {product.model && <div><strong>{dt.model}:</strong> {product.model}</div>}
                {product.fault && <div><strong>{dt.fault}:</strong> {translatedFault}</div>}
                {product.deliveryTime && <div><strong>{dt.deliveryTime}:</strong> {translatedDeliveryTime}</div>}
              </div>
              {product.brandLogo && (
                <div className="service-brand-logo-container" style={{ width: '80px', height: '80px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffffff', borderRadius: '8px', padding: '6px', border: '1px solid #edf2f7' }}>
                  <img src={product.brandLogo} alt={`${product.brandName} logo`} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                </div>
              )}
            </div>
          )}

          {/* Action Row: Quantity Selector & Add to Cart */}
          <div className="purchase-action-row">
            <div className="quantity-selector">
              <button className="qty-btn minus" onClick={handleDecrease} aria-label="Decrease quantity">−</button>
              <span className="qty-number">{quantity}</span>
              <button className="qty-btn plus" onClick={handleIncrease} aria-label="Increase quantity">+</button>
            </div>

            <button
              className="add-to-cart-btn-primary"
              onClick={(e) => handleAddToCart(product, quantity, e)}
              disabled={!product.isService && (product.stockStatus === 'Out of Stock' || product.quantity <= 0)}
              style={(!product.isService && (product.stockStatus === 'Out of Stock' || product.quantity <= 0)) ? { opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#718096' } : {}}
            >
              {(!product.isService && (product.stockStatus === 'Out of Stock' || product.quantity <= 0)) ? t.outOfStock : t.addToCart}
            </button>
          </div>

          {/* Static Specifications Block */}
          {product.specification && (
            <div className="product-static-specifications" style={{ margin: '25px 0', borderTop: '1px solid #edf2f7', paddingTop: '20px' }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#1a202c', fontSize: '18px', fontWeight: '700' }}>
                {dt.specifications}
              </h3>
              <div 
                className="specifications-text" 
                style={{ 
                  whiteSpace: 'pre-line', 
                  color: '#4a5568', 
                  fontSize: '14px', 
                  lineHeight: '1.6',
                  ...(!isSpecExpanded && needsSpecCollapse ? {
                    display: '-webkit-box',
                    WebkitLineClamp: 5,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  } : {})
                }}
              >
                {translatedSpec}
              </div>
              
              {needsSpecCollapse && (
                <div style={{ marginTop: '8px' }}>
                  {!isSpecExpanded ? (
                    <button 
                      onClick={() => setIsSpecExpanded(true)} 
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        color: '#be1e2d', 
                        fontWeight: '600', 
                        cursor: 'pointer', 
                        padding: 0,
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      Show more
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </button>
                  ) : (
                    <button 
                      onClick={() => setIsSpecExpanded(false)} 
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        color: '#be1e2d', 
                        fontWeight: '600', 
                        cursor: 'pointer', 
                        padding: 0,
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      Show less
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="18 15 12 9 6 15"></polyline>
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Accordion List */}
          <div className="info-accordion-list">

            {/* Accordion 2: Shipping */}
            <div className="info-accordion-item">
              <button
                className="accordion-header-btn"
                onClick={() => setActiveTab(prev => ({ ...prev, shipping: !prev.shipping }))}
                aria-expanded={activeTab.shipping}
              >
                <span>{t.shippingAndWarranty}</span>
                <svg className={`accordion-arrow-icon ${activeTab.shipping ? 'rotated' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              <div className={`accordion-collapse-panel ${activeTab.shipping ? 'open' : ''}`}>
                <div className="accordion-body-text">

                  {/* Grid details shown in the screenshot */}
                  <div className="shipping-details-grid">

                    <div className="ship-grid-item">
                      <div className="ship-icon-circle">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="1" x2="12" y2="23" />
                          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                        </svg>
                      </div>
                      <div className="ship-meta-text">
                        <span className="ship-meta-label">{t.discountLabel}</span>
                        <span className="ship-meta-val">{t.promoDiscount}</span>
                      </div>
                    </div>

                    <div className="ship-grid-item">
                      <div className="ship-icon-circle">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="21 16 12 21 3 16 12 11 21 16" />
                          <polyline points="3 8 12 13 21 8" />
                          <line x1="12" y1="3" x2="12" y2="13" />
                        </svg>
                      </div>
                      <div className="ship-meta-text">
                        <span className="ship-meta-label">{t.packageLabel}</span>
                        <span className="ship-meta-val">{t.packageDesc}</span>
                      </div>
                    </div>

                    <div className="ship-grid-item">
                      <div className="ship-icon-circle">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="1" y="3" width="15" height="13" />
                          <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                          <circle cx="5.5" cy="18.5" r="2.5" />
                          <circle cx="18.5" cy="18.5" r="2.5" />
                        </svg>
                      </div>
                      <div className="ship-meta-text">
                        <span className="ship-meta-label">{t.deliveryTimeLabel}</span>
                        <span className="ship-meta-val">{translatedDeliveryTime || t.deliveryTimeDesc}</span>
                      </div>
                    </div>

                    <div className="ship-grid-item">
                      <div className="ship-icon-circle">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        </svg>
                      </div>
                      <div className="ship-meta-text">
                        <span className="ship-meta-label">{t.warrantyLabel}</span>
                        <span className="ship-meta-val">{t.warrantyDesc}</span>
                      </div>
                    </div>

                  </div>

                </div>
              </div>
            </div>

          </div>

        </div>

      </div>


      {/* Lightbox Modal */}
      {isLightboxOpen && (
        <div 
          className="lightbox-overlay"
          onClick={() => setIsLightboxOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            userSelect: 'none',
          }}
        >
          {/* Close Button */}
          <button
            onClick={() => setIsLightboxOpen(false)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              color: '#ffffff',
              fontSize: '24px',
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s',
              zIndex: 10001,
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
          >
            ✕
          </button>

          {/* Zoom Controls Toolbar */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: '10px',
              background: 'rgba(255, 255, 255, 0.1)',
              padding: '6px 16px',
              borderRadius: '30px',
              zIndex: 10001,
            }}
          >
            <button
              onClick={() => setZoomScale(prev => Math.max(1, prev - 0.3))}
              style={{ background: 'none', border: 'none', color: '#fff', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer', padding: '0 8px' }}
              title="Zoom Out"
            >
              －
            </button>
            <span style={{ color: '#fff', fontSize: '14px', alignSelf: 'center', minWidth: '45px', textAlign: 'center' }}>
              {Math.round(zoomScale * 100)}%
            </span>
            <button
              onClick={() => setZoomScale(prev => Math.min(3, prev + 0.3))}
              style={{ background: 'none', border: 'none', color: '#fff', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer', padding: '0 8px' }}
              title="Zoom In"
            >
              ＋
            </button>
            <button
              onClick={() => setZoomScale(1)}
              style={{ background: 'none', border: 'none', color: '#a0aec0', fontSize: '12px', cursor: 'pointer', padding: '0 8px', marginLeft: '6px' }}
            >
              Reset
            </button>
          </div>

          {/* Left Arrow */}
          {mediaItems.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex(prev => (prev === 0 ? mediaItems.length - 1 : prev - 1));
                setZoomScale(1); // reset zoom
              }}
              style={{
                position: 'absolute',
                left: '20px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                color: '#ffffff',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s',
                zIndex: 10001,
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
          )}

          {/* Image/Video Container */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '85vw',
              maxHeight: '80vh',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.1s ease',
            }}
          >
            {mediaItems[lightboxIndex]?.type === 'video' ? (
              <video
                src={mediaItems[lightboxIndex]?.url}
                controls
                autoPlay
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  transform: `scale(${zoomScale})`,
                  transition: 'transform 0.2s ease',
                }}
              />
            ) : (
              <img
                src={mediaItems[lightboxIndex]?.url}
                alt="Product zoom view"
                onClick={() => setZoomScale(prev => (prev === 1 ? 2.2 : 1))}
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  cursor: zoomScale === 1 ? 'zoom-in' : 'zoom-out',
                  transform: `scale(${zoomScale})`,
                  transition: 'transform 0.2s ease',
                  borderRadius: '4px',
                }}
              />
            )}
          </div>

          {/* Right Arrow */}
          {mediaItems.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex(prev => (prev === mediaItems.length - 1 ? 0 : prev + 1));
                setZoomScale(1); // reset zoom
              }}
              style={{
                position: 'absolute',
                right: '20px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                color: '#ffffff',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s',
                zIndex: 10001,
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          )}

          {/* Bottom Thumbnails Navigation inside Lightbox */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              bottom: '20px',
              display: 'flex',
              gap: '8px',
              zIndex: 10001,
              background: 'rgba(0, 0, 0, 0.4)',
              padding: '6px 12px',
              borderRadius: '8px',
            }}
          >
            {mediaItems.map((item, idx) => (
              <div
                key={idx}
                onClick={() => {
                  setLightboxIndex(idx);
                  setZoomScale(1);
                }}
                style={{
                  width: '45px',
                  height: '45px',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: idx === lightboxIndex ? '2px solid #be1e2d' : '1px solid rgba(255,255,255,0.3)',
                  opacity: idx === lightboxIndex ? 1 : 0.6,
                  transition: 'border-color 0.2s, opacity 0.2s',
                }}
              >
                {item.type === 'video' ? (
                  <div style={{ width: '100%', height: '100%', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff">
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                  </div>
                ) : (
                  <img src={item.url} alt={`Thumbnail ${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
